import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CitationCollapsoidComponent } from './citation-collapsoid/citation-collapsoid.component';
import { SessionService } from '@app/security/session.service';
import { Client, ProductGroup } from '@app/core/models/client';
import { FocusAiService } from '../../services/focus-ai.service';
import { User } from '@app/core/models/user';
import { MessageClass } from '@app/core/models/message';
import { FormatUtils } from '@app/core/utils/format-utils';
import { ContentProLibraryService } from '@app/content-pro-library/services/content-pro-library.service';

export interface Citation {
  file_name: string;
  updated_at: string;
}

export interface ChatMessage {
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
  citations?: Citation[];
}

@Component({
  selector: 'rn-content-pro-chat',
  standalone: true,
  imports: [ReactiveFormsModule, CitationCollapsoidComponent],
  templateUrl: './content-pro-chat.component.html',
  styleUrl: './content-pro-chat.component.scss',
})
export class ContentProChatComponent implements OnInit, OnDestroy {
  @Input({ required: true }) messageId: string;
  @Input({ required: true }) emptyMessageContent: boolean;
  @Input({ required: true }) productGroup: ProductGroup;
  @Input({ required: true }) clientId: string;
  @Input({ required: true }) language: string;

  @Output() contentProMessage = new EventEmitter<MessageClass>();
  @Output() contentProError = new EventEmitter<string>();
  @Output() contentProCancel = new EventEmitter();

  @ViewChild('chatMessages') chatMessagesContainer: ElementRef;

  currentClient: Client;
  currentUser: User;
  hasUserAcceptedTermsAndConditions = false;
  hasContentProFiles = false;
  prompt = new FormControl<string>('');

  // Chat interface state
  conversation: ChatMessage[] = [];
  isGenerating = false;

  // Subscription for cancellation
  private generateSubscription: Subscription | null = null;

  // Store previous state for iterative message generation
  prevState?: {
    text: string;
    actions: { type: string; label: string; value: string }[];
  };

  private sessionService = inject(SessionService);
  private focusAiService = inject(FocusAiService);
  private contentProLibraryService = inject(ContentProLibraryService);

  ngOnDestroy(): void {
    this.stopGeneration();
  }

  ngOnInit(): void {
    this.currentClient = this.sessionService.getCurrentUsersClient();
    this.currentUser = this.sessionService.currentUser;
    this.checkUserAcceptedTermsAndConditions();
    this.checkContentProFiles();
    this.initializeChat();
  }

  initializeChat(): void {
    // Clear conversation
    this.conversation = [];
  }

  checkUserAcceptedTermsAndConditions() {
    this.focusAiService.deleteLegacyTermsCookie(
      this.currentUser?.email_address,
      this.currentClient?.id,
      'chat',
    );
    this.hasUserAcceptedTermsAndConditions =
      this.focusAiService.hasUserAcceptedTermsAndConditions(
        this.currentUser?.id,
        this.currentClient?.id,
        'chat',
      );
  }

  checkContentProFiles(): void {
    if (this.currentClient) {
      this.contentProLibraryService.getFiles(this.currentClient.id).subscribe({
        next: (response) => {
          this.hasContentProFiles = response.data && response.data.length > 0;
        },
        error: () => {
          this.hasContentProFiles = false;
        },
      });
    }
  }

  acceptAndContinueToContentPro() {
    if (!this.currentClient || !this.currentUser) return;

    this.focusAiService.acceptTermsAndConditions(
      this.currentUser?.id,
      this.currentClient?.id,
      'chat',
    );

    this.checkUserAcceptedTermsAndConditions();
  }

  cancel() {
    this.resetChat();
    this.contentProCancel.emit();
  }

  resetChat(): void {
    this.stopGeneration();
    this.initializeChat();
    this.prevState = undefined;
    this.isGenerating = false;
    this.prompt.setValue('');
  }

  stopGeneration(): void {
    if (this.generateSubscription) {
      this.generateSubscription.unsubscribe();
      this.generateSubscription = null;
    }
    this.focusAiService.turnOffGenerating();
    this.isGenerating = false;
  }

  sendMessage(): void {
    if (this.prompt.invalid || !this.prompt.value?.trim() || this.isGenerating)
      return;

    const userMessage: ChatMessage = {
      type: 'user',
      content: this.prompt.value.trim(),
      timestamp: new Date(),
    };

    this.conversation.push(userMessage);
    const userPrompt = this.prompt.value.trim();
    this.prompt.setValue('');

    // Reset textarea height
    const textarea = document.getElementById(
      'contentProPrompt',
    ) as HTMLTextAreaElement;
    if (textarea) {
      textarea.style.height = 'auto';
    }

    this.scrollToBottom();
    this.isGenerating = true;

    // Call actual API to generate message
    this.generateSubscription = this.focusAiService
      .generate(
        this.clientId,
        this.messageId,
        userPrompt,
        this.productGroup.id.toString(),
        this.prevState,
      )
      .subscribe({
        next: (data: {
          text: string;
          actions: { type: string; label: string; value: string }[];
          summary: string;
          citations?: Citation[];
        }) => {
          // Store response for next iteration
          this.prevState = data;

          const aiMessage: ChatMessage = {
            type: 'ai',
            content: data.summary,
            timestamp: new Date(),
            citations: data.citations || [],
          };
          this.conversation.push(aiMessage);
          this.isGenerating = false;
          this.generateSubscription = null;

          // Scroll to show new AI message
          this.scrollToBottom();

          // Generate the actual message for preview
          this.generateMessageFromApiResponse(data);
        },
        error: (error) => {
          console.error('Error generating message:', error);
          const errorMessage: ChatMessage = {
            type: 'ai',
            content:
              'Sorry, there was an error generating the message. Please try again.',
            timestamp: new Date(),
          };
          this.conversation.push(errorMessage);
          this.isGenerating = false;
          this.generateSubscription = null;
          this.scrollToBottom();
        },
      });
  }

  generateMessageFromApiResponse(data: {
    text: string;
    actions: { type: string; label: string; value: string }[];
  }): void {
    // Create a MessageClass object from the API response for the preview
    const newMessage = new MessageClass({ showSmsByDefault: true });
    newMessage.wire.text = data.text;
    newMessage.wire.ai_generated = true;

    // Add actions from API response (similar to v1 implementation)
    data.actions.forEach((action, index) => {
      const isPrimaryAction = index === 0;
      if (action.type === 'hyperlink') {
        newMessage.addLinkElement(
          this.currentClient.fdic_enabled,
          isPrimaryAction,
          action,
        );
      } else if (action.type === 'collapsible') {
        newMessage.addCollapsibleElement(action);
      } else if (action.type === 'call') {
        const val = FormatUtils.formatPhoneNumber(action.value);
        newMessage.addCallElement(isPrimaryAction, {
          ...action,
          value: val,
        });
      } else if (action.type === 'email') {
        newMessage.addEmailElement(isPrimaryAction, action);
      } else if (action.type === 'contact_card') {
        newMessage.addContactCardElement(isPrimaryAction, action);
      } else if (action.type === 'form_info_capture') {
        newMessage.addFormInfoCaptureElement(
          this.currentClient.feed_enabled,
          isPrimaryAction,
          action,
        );
      } else if (action.type === 'two_way') {
        newMessage.addTwoWayElement(isPrimaryAction, action);
      } else if (action.type === 'consent_upgrade') {
        if (this.productGroup?.consent?.in_wire_upgrade?.ts_cs) {
          newMessage.addIWCUElement(
            this.productGroup.consent.in_wire_upgrade.ts_cs,
            this.currentClient.feed_enabled,
            isPrimaryAction,
            action,
          );
        }
      }
    });

    this.contentProMessage.emit(newMessage);
  }

  autoExpandTextarea(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.chatMessagesContainer) {
        const element = this.chatMessagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    }, 0);
  }
}
